import type { MouseEventHandler, ReactNode } from "react";
import { Link as RouterLink, type LinkProps as RouterLinkProps } from "react-router";

import { twResolve } from "@/helpers/tw-resolve";

import type { DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";

const LINK_VARIANT_CLASSES = {
  caption: "text-caption",
  body: "text-body",
  "body-emphasis": "text-body-emphasis",
  heading: "text-heading-3",
} as const;

const LINK_TONE_CLASSES = {
  default: "text-text-primary",
  primary: "text-action-primary hover:text-action-primary-hover pressed:text-action-primary-pressed",
  light: "text-action-light hover:text-action-light-hover pressed:text-action-light-pressed",
} as const;

const LINK_UNDERLINE_CLASSES = {
  always: "underline",
  hover: "no-underline hover:underline",
  none: "no-underline",
} as const;

const LINK_BASE_CLASS =
  "inline-flex w-fit items-baseline underline-offset-2 transition-colors duration-fast motion-reduce:transition-none focus-visible:rounded-xs focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 focus-visible:outline-hidden";

export type LinkVariant = keyof typeof LINK_VARIANT_CLASSES;
export type LinkTone = keyof typeof LINK_TONE_CLASSES;
export type LinkUnderline = keyof typeof LINK_UNDERLINE_CLASSES;

type LinkNavigationProps = { external?: false; state?: RouterLinkProps["state"] } | { external: true; state?: never };

export type LinkProps = {
  children: ReactNode;
  href: string;
  disabled?: boolean;
  fullWidth?: boolean;
  variant?: LinkVariant;
  tone?: LinkTone;
  underline?: LinkUnderline;
} & LinkNavigationProps &
  Omit<DesignSystemElementProps<"a">, "aria-disabled" | "href" | "onClick"> & {
    onClick?: MouseEventHandler<HTMLAnchorElement>;
  } & DesignSystemRef<HTMLAnchorElement>;

export function Link({
  children,
  disabled = false,
  external = false,
  fullWidth = false,
  href,
  onClick,
  ref,
  rel,
  state,
  target,
  tone = "primary",
  underline = "always",
  variant = "body",
  ...nativeProps
}: LinkProps): ReactNode {
  const className = twResolve(
    LINK_BASE_CLASS,
    LINK_VARIANT_CLASSES[variant],
    LINK_TONE_CLASSES[tone],
    LINK_UNDERLINE_CLASSES[underline],
    fullWidth && "w-full",
    disabled && "pointer-events-none text-text-disabled",
  );

  if (disabled) {
    return (
      <a {...nativeProps} ref={ref} role="link" aria-disabled="true" tabIndex={-1} className={className}>
        {children}
      </a>
    );
  }

  if (external) {
    return (
      <a
        {...nativeProps}
        ref={ref}
        href={href}
        target={target}
        rel={rel ?? (target === "_blank" ? "noopener noreferrer" : undefined)}
        onClick={onClick}
        className={className}
      >
        {children}
      </a>
    );
  }

  return (
    <RouterLink
      {...nativeProps}
      ref={ref}
      to={href}
      state={state}
      target={target}
      rel={rel}
      onClick={onClick}
      className={className}
    >
      {children}
    </RouterLink>
  );
}
